// sessionStorage cache for the computed /quiz/answersweb result.
//
// Extracted from ProductSuggestion.jsx (SHI-554) so the save path can invalidate
// it. Previously nothing did, and the only removal was TTL expiry — so for ten
// minutes after the cache was written the product-suggestion page rendered a
// pre-edit snapshot and never called the API at all. A customer who changed a
// dose, went to checkout and came back saw their old formula, and a hard refresh
// did not help because sessionStorage survives reloads.

const ANSWERS_WEB_CACHE_PREFIX = 'answers_web_result';
const ANSWERS_WEB_CACHE_TTL_MS = 10 * 60 * 1000;

export const getAnswersWebCacheKey = (quizId, lang) =>
  `${ANSWERS_WEB_CACHE_PREFIX}:${Number(quizId || 0)}:${lang || 'en'}`;

export const getCachedAnswersWebResult = (quizId, lang) => {
  if (typeof window === 'undefined' || Number(quizId) <= 0) return null;

  try {
    const cached = sessionStorage.getItem(getAnswersWebCacheKey(quizId, lang));
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    if (!parsed?.data || Date.now() - parsed.createdAt > ANSWERS_WEB_CACHE_TTL_MS) {
      sessionStorage.removeItem(getAnswersWebCacheKey(quizId, lang));
      return null;
    }

    return parsed.data;
  } catch (error) {
    return null;
  }
};

export const setCachedAnswersWebResult = (quizId, lang, data) => {
  if (typeof window === 'undefined' || Number(quizId) <= 0 || !data) return;

  try {
    sessionStorage.setItem(
      getAnswersWebCacheKey(quizId, lang),
      JSON.stringify({
        createdAt: Date.now(),
        data,
      }),
    );
  } catch (error) {
    // Storage can fail in private mode or quota limits; the live response still hydrates the page.
  }
};

// Drop every cached result once the formula has been edited.
//
// Deliberately clears all quiz ids and all languages rather than just the one
// being saved. The page's active quiz id and the id the changes are saved under
// are resolved separately, and the cache is keyed per language, so a targeted
// delete can miss the entry that is actually about to be read back. The cost of
// over-clearing is one recompute; the cost of under-clearing is the customer
// seeing a stale formula, which is the bug this exists to prevent.
export const clearCachedAnswersWebResults = () => {
  if (typeof window === 'undefined') return;

  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(`${ANSWERS_WEB_CACHE_PREFIX}:`))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    // Nothing to do — a cache we cannot clear just means one stale render at worst.
  }
};
